import { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";

import {
  BalanceOperationTab,
  BalanceOperationTabAction,
  Icons,
  Roles,
  TransactionType,
  UserType,
} from "../../../../../enums";
import { Button, Currency, Dropdown, NumberInput } from "../../../../components/shared";
import { useForm, useQuery, useStore, useTranslation } from "../../../../../hooks";
import { RolesDropdownData } from "../../../../../constants/roles-dropdown-data";
import { DEFAULT_TAKE, RegexConstants, T } from "../../../../../constants";
import { dataToDropDownConverter } from "../../../../../helpers";
import Input from "../../../../components/shared/input";
import { Toaster } from "../../../../containers";


interface ModalContentProps {
  // pinCode: string;
  id: number,
  userTypeBalance?: UserType;
  openWithHeader?: boolean;
  currentBalance?: number;
  onClose: () => void;
  currencyCode?: string;
  errorMessage?: string;
  isShowConvertAmountSection?: boolean;
}

const initialValues = {
  type: "",
  amount: "",
  pinCode: "",
  comment: "",
  userType: "",
  assigneeId: "",
};

const BalanceModalContent: React.FC<ModalContentProps> = observer(({
     id,
     // pinCode,
     onClose,
     userTypeBalance,
     errorMessage,
     currentBalance,
     currencyCode = "",
     openWithHeader = false,
     isShowConvertAmountSection,
}) => {

    const t = useTranslation();

    const {
      usersStore,
      walletStore,
      partnerStore: { selectedBusiness },
      currenciesStore: { currenciesMap },
      businessStore: { businessById: { data: { balance: businessBalance } } },
      userSettingsStore: { userSettings: { data: { userType, isPinRequiredForBalanceChange } } },
    } = useStore();

    const {
      userById: { data: { balance: userBalance } },
      usersByRole: { data: { items: usersByRole } },
    } = usersStore;

    const balance =  isShowConvertAmountSection ? businessBalance : userBalance;

    const { name } = currenciesMap[currencyCode] || {};

    const { query } = useQuery();
    const [isShowPinCode, setShowPinCode] = useState(false);
    const [activeTab, setActiveTab] = useState(openWithHeader ? BalanceOperationTab.TransFer : BalanceOperationTab.Encashment);
    const [activeTabAction, setActiveTabAction] = useState(BalanceOperationTabAction.SendMoneyToOrCashIn);
    const { values, setValues, handleDropdownChange, handleInputChange } = useForm(initialValues);
    const skip = +query.skip || 0;
    const take = +query.take || DEFAULT_TAKE;

  useEffect(() => {
    return () => {
      usersStore.resetUsersByRole();
    }
  }, []);

    const handleChangeActiveTabAction = useCallback((actionType: BalanceOperationTabAction) => {
      setActiveTabAction(actionType);
      usersStore.resetUsersByRole();
      setShowPinCode(false);
      setValues(initialValues);
    }, []);

    const handleChangeActiveTab = useCallback((tab: BalanceOperationTab) => {
      setActiveTab(tab);
      usersStore.resetUsersByRole();
      setValues(initialValues);
      setShowPinCode(false);
      setActiveTabAction(BalanceOperationTabAction.SendMoneyToOrCashIn);
    }, []);

    const fieldsConfig = useMemo(() => {
      const isSendMoneyTo = activeTabAction === BalanceOperationTabAction.SendMoneyToOrCashIn;
      return {
        rolDropDownLabel: isSendMoneyTo ? t(T.RECEIVER_ROLE) : t(T.SENDER_ROLE),
        receiverOrSenderDropdownLabel: isSendMoneyTo ? t(T.RECEIVER) : t(T.SENDER),
      }
    }, [activeTabAction,t]);

  const rolesDropdownData = useMemo(() => {
    if (userType && +userType === Roles.Manager) {
      return RolesDropdownData;
    }
    return RolesDropdownData.filter((role) => +role.value !== userType);
  }, [userType]);

    const handleReceiverRoleDropdownChange = useCallback((value: number) => {
      setValues((prevValues) => ({ ...prevValues, userType: value }));
      usersStore.getUsersByRole({ userType: value });
    }, []);

    const receiversDropdownData = useMemo(() => {
      return dataToDropDownConverter(usersByRole, {labelKey: "userName", valueKey: "id"});
    }, [usersByRole]);

    const actionButtonName = useMemo(() => {
      return activeTab === BalanceOperationTab.TransFer || (activeTab === BalanceOperationTab.Encashment && activeTabAction === BalanceOperationTabAction.SendMoneyToOrCashIn) ?
        t(T.DEPOSIT) : t(T.WITHDRAW);
    }, [activeTab, activeTabAction]);

    const disabledActionButton = useMemo(() => {
      const { amount, pinCode, assigneeId } = values;
      const pinRequired = isPinRequiredForBalanceChange && !pinCode;
      return activeTab === BalanceOperationTab.TransFer ? !amount || pinRequired || !assigneeId : !amount || pinRequired;
    }, [values]);

  const getUsers = useCallback(() => {
    const { partnerId,...restQuery} = query;
    usersStore.getUsers({
      ...restQuery,
      skip,
      take,
      onlyTopManagers: true,
      partnerIds: partnerId ? [partnerId] : [],
      businessUnitIds: selectedBusiness.id ? [selectedBusiness.id] : query.businessUnitId ? [query.businessUnitId] : [],
      userType: userTypeBalance || UserType.Manager,
    });
  }, [query, skip, take, selectedBusiness?.id, userTypeBalance]);

    const handleBalanceTransaction = useCallback(() => {

      if (activeTab === BalanceOperationTab.Encashment && activeTabAction === BalanceOperationTabAction.SendMoneyToOrCashIn) {
        walletStore.cashInBalance({
          cashierId: id,
          comment: values.comment,
          amount: +values.amount,
          pinCode: +values.pinCode,
        }).then(() => {
          Toaster.success({ message: T.TRANSACTION_SUCCESSFULLY_MESSAGE });
          getUsers?.();
        }).catch((error) => {
            Toaster.error({ message: error.errorMessage });
          }).finally(() => {
          onClose();
        });
        return;
      }

      if (activeTab === BalanceOperationTab.Encashment && activeTabAction === BalanceOperationTabAction.GetMoneyFromOrCashOut) {
        walletStore.cashOutBalance({
          cashierId: id,
          amount: +values.amount,
          comment: values.comment,
          pinCode: +values.pinCode,
        }).then(() => {
          Toaster.success({ message: T.TRANSACTION_SUCCESSFULLY_MESSAGE });
          getUsers?.();
        }).catch((error) => {
          Toaster.error({ message: error.errorMessage });
        }).finally(() => {
          onClose();
        });
        return;
      }

      if (activeTab === BalanceOperationTab.TransFer) {
        walletStore.balanceTransaction({
          ...values,
          pinCode: +values.pinCode,
          amount: +values.amount,
          type: activeTabAction === BalanceOperationTabAction.SendMoneyToOrCashIn ? TransactionType.Send : TransactionType.Receive,
        }).then(() => {
          Toaster.success({ message: T.TRANSACTION_SUCCESSFULLY_MESSAGE });
          getUsers?.();
        }).catch((error) => {
          Toaster.error({ message: error.errorMessage });
        }).finally(() => {
          onClose();
        });
      }
    }, [values, activeTabAction, activeTab, getUsers]);

  const handleCommentChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.target;
    if (value.length > 100) return;
    setValues((prevValues) => ({ ...prevValues, comment: value }));
  }, []);

  const handlePinCode = useCallback((value: string) => {
    if (RegexConstants.Number.test(value) && value.length <= 5) {
      setValues((prevValues) => ({...prevValues, pinCode: value}));
    }
  }, []);

    return (
      <div className="edit-balance-holder">
        <div className="edit-balance-container">

          <div className="popup-tab-wrapper">
            {
              openWithHeader && (
                <button
                  disabled={!openWithHeader}
                  className={`popup-tab-el ${activeTab === BalanceOperationTab.TransFer ? "active" : ""}`}
                  type="button"
                  onClick={() => handleChangeActiveTab(BalanceOperationTab.TransFer)}
                >
                  {t(T.TRANSFER)}
                </button>
              )
            }
            <button
              type="button"
              onClick={() => handleChangeActiveTab(BalanceOperationTab.Encashment)}
              className={`popup-tab-el ${activeTab === BalanceOperationTab.Encashment ? "active" : ""}`}
            >
              {t(T.ENCASHMENT)}
            </button>
          </div>

          <div className="popup-tabs-holder">
            <button
              type="button"
              onClick={() => handleChangeActiveTabAction(BalanceOperationTabAction.SendMoneyToOrCashIn)}
              className={`popup-tab ${activeTabAction === BalanceOperationTabAction.SendMoneyToOrCashIn ? "active" : ""}`}
            >
              {activeTab === BalanceOperationTab.TransFer ? t(T.SEND_MONEY_TO) : t(T.CASH_IN)}
            </button>
            <button
              type="button"
              onClick={() => handleChangeActiveTabAction(BalanceOperationTabAction.GetMoneyFromOrCashOut)}
              className={`popup-tab ${activeTabAction === BalanceOperationTabAction.GetMoneyFromOrCashOut ? "active" : ""}`}
            >
              {activeTab === BalanceOperationTab.TransFer ? t(T.GET_MONEY_FROM) : t(T.CASH_OUT)}
            </button>
            <span className="popup-active-tab-bg"/>
          </div>

          <div className="popups-tabs-content">
            <dl className="popup-balance-info">
              <div className="popup-balance-row">
                <dt className="popup-balance-title">
                  {t(T.CURRENT_BALANCE)}
                </dt>
                <dd className="popup-balance-val">
                  <Currency
                    convert={false}
                    currencyCode={name}
                    value={currentBalance || balance || 0}
                  />
                </dd>
              </div>
            </dl>
            {
              activeTab === BalanceOperationTab.TransFer && (
                <>
                  <Dropdown
                    value={values.userType}
                    data={rolesDropdownData}
                    className="receiver-dropdown"
                    label={fieldsConfig.rolDropDownLabel}
                    onChange={(value) => handleReceiverRoleDropdownChange(value as number)}
                  />
                  <Dropdown
                    value={values.assigneeId}
                    data={receiversDropdownData}
                    className="receiver-dropdown"
                    label={fieldsConfig.receiverOrSenderDropdownLabel}
                    onChange={(value) => handleDropdownChange(value as number, "assigneeId")}
                  />
                </>
              )
            }
            <NumberInput
              label={t(T.AMOUNT)}
              value={values.amount}
              placeholder={t(T.AMOUNT)}
              errorMessage={errorMessage}
              numberRegEx={RegexConstants.NonNegativeNumbers}
              onChange={(value) => handleInputChange(value, "amount")}
            />
            <div className="balance-popup-field">
              <Input
                autoComplete="off"
                label={t(T.PIN_CODE)}
                value={values.pinCode}
                placeholder={t(T.PIN_CODE)}
                type={isShowPinCode ? "text" : "password"}
                onIconClick={() => setShowPinCode(!isShowPinCode)}
                onChange={(e) => handlePinCode(e.target.value)}
                icon={values.pinCode?.length ? isShowPinCode ? Icons.VisibilityOn : Icons.VisibilityOff : null}
              />
            </div>
            {
              activeTab === BalanceOperationTab.Encashment && (
                <div className="balance-popup-field">
                  <textarea
                    value={values.comment}
                    placeholder={t(T.COMMENT)}
                    onChange={(value) => handleCommentChange(value)}
                  />
                  <span className='textarea-field-placeholder'>{t(T.COMMENT)}</span>
                </div>
              )
            }
          </div>
        </div>
        <div className="button-balance-case">
          <Button
            text={actionButtonName}
            disabled={disabledActionButton}
            onClick={handleBalanceTransaction}
          />
        </div>
      </div>
    );
  },
);

export default BalanceModalContent;